import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import './styles.css';

const SearchFilterDropdown = ({ defaultFacets }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({});
  const [facetsData, setFacetsData] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const dropdownRef = useRef(null);

  // Cargar datos de facets disponibles desde la API de búsqueda (igual que en la página de resultados)
  useEffect(() => {
    const fetchFacetsData = async () => {
      try {
        const facetKeys = Object.keys(defaultFacets);
        const allFacetsData = {};

        // Cargar cada tipo de facet desde el endpoint rápido /api/search-fast.
        // El endpoint nativo de DKAN (/search/facets) se cuelga con el volumen
        // actual; este lee el índice de búsqueda directamente (ms).
        await Promise.all(
          facetKeys.map(async (facetKey) => {
            try {
              const response = await fetch(
                `/api/search-fast/facets?facets=${facetKey}`
              );
              const data = await response.json();

              // Estructura: { facets: [ { type, name, total }, ... ] } (plano)
              const values = (data && Array.isArray(data.facets)) ? data.facets : [];
              if (values.length > 0) {
                allFacetsData[facetKey] = values.map(facet => ({
                  value: facet.name || facet.value || '',
                  label: facet.name || facet.value || '',
                  count: facet.total || facet.count || 0
                }));
              }
            } catch (error) {
              console.error(`Error loading facet ${facetKey}:`, error);
            }
          })
        );

        setFacetsData(allFacetsData);
      } catch (error) {
        console.error('Error loading facets:', error);
      }
    };

    fetchFacetsData();
  }, [defaultFacets]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => {
      const newFilters = { ...prev };
      if (!newFilters[filterType]) {
        newFilters[filterType] = [];
      }

      const index = newFilters[filterType].indexOf(value);
      if (index > -1) {
        newFilters[filterType].splice(index, 1);
        if (newFilters[filterType].length === 0) {
          delete newFilters[filterType];
        }
      } else {
        newFilters[filterType].push(value);
      }

      return newFilters;
    });
  };

  const toggleSection = (filterType) => {
    setExpandedSections(prev => ({
      ...prev,
      [filterType]: !prev[filterType]
    }));
  };

  const clearFilters = () => {
    setSelectedFilters({});
  };

  const applyFilters = () => {
    // Solo cerrar el dropdown - los filtros se aplican al hacer búsqueda
    setIsOpen(false);
  };

  // Interceptar el submit del formulario para agregar los filtros
  useEffect(() => {
    const form = document.querySelector('.search-form');
    if (!form) return;

    const handleSubmit = (e) => {
      e.preventDefault();
      
      // Verificar si hay texto en el input
      const searchInput = form.querySelector('.search-input');
      const hasText = searchInput && searchInput.value.trim().length > 0;
      
      // Verificar si hay filtros seleccionados
      const hasFilters = Object.keys(selectedFilters).length > 0 && 
                         Object.values(selectedFilters).some(arr => arr.length > 0);
      
      // Validar que haya al menos texto o filtros
      if (!hasText && !hasFilters) {
        alert('Por favor ingresa un término de búsqueda o selecciona al menos un filtro');
        return;
      }
      
      // Construir URL con filtros seleccionados (formato igual al de la página de búsqueda)
      const params = new URLSearchParams();
      
      // Agregar fulltext si existe
      if (hasText) {
        params.append('fulltext', searchInput.value.trim());
      }

      // Agregar filtros seleccionados - los valores múltiples se separan con comas
      for (const [filterType, values] of Object.entries(selectedFilters)) {
        if (values.length > 0) {
          // Usar join con comas como hace la búsqueda (arrayFormat: 'comma')
          params.append(filterType, values.join(','));
        }
      }

      // Redirigir a la página de búsqueda
      const url = `/search/?${params.toString()}`;
      // eslint-disable-next-line no-restricted-globals, no-restricted-syntax
      window.location.href = url;
    };

    form.addEventListener('submit', handleSubmit);
    return () => form.removeEventListener('submit', handleSubmit);
  }, [selectedFilters]);

  const filterCount = Object.values(selectedFilters).reduce(
    (acc, arr) => acc + arr.length, 0
  );

  return (
    <div className="search-filter-dropdown" ref={dropdownRef} style={{ flexShrink: 0 }}>
      <button 
        className="filter-dropdown-button"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
        style={{ whiteSpace: 'nowrap' }}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="18" 
          height="18" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
        </svg>
        <span>Filtros</span>
        {filterCount > 0 && (
          <span className="filter-badge">{filterCount}</span>
        )}
        <svg 
          className={`dropdown-arrow ${isOpen ? 'open' : ''}`}
          xmlns="http://www.w3.org/2000/svg" 
          width="14" 
          height="14" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>

      {isOpen && (
        <div className="filter-dropdown-menu">
          <div className="filter-dropdown-header">
            <h3>Filtros de búsqueda</h3>
            {filterCount > 0 && (
              <button 
                className="clear-filters-btn"
                onClick={clearFilters}
                type="button"
              >
                Limpiar todo
              </button>
            )}
          </div>

          <div className="filter-dropdown-content">
            {Object.entries(defaultFacets).map(([filterType, config]) => {
              const facetOptions = facetsData[filterType] || [];
              const isExpanded = expandedSections[filterType] || false;
              const filterCount = selectedFilters[filterType]?.length || 0;
              
              return (
                <div key={filterType} className="filter-section">
                  <button 
                    className="filter-section-header"
                    onClick={() => toggleSection(filterType)}
                    type="button"
                  >
                    <div className="filter-section-title">
                      <h4>{config.label}</h4>
                      {filterCount > 0 && (
                        <span className="section-filter-badge">{filterCount}</span>
                      )}
                    </div>
                    <svg 
                      className={`section-arrow ${isExpanded ? 'expanded' : ''}`}
                      xmlns="http://www.w3.org/2000/svg" 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  
                  {isExpanded && (
                    <div className="filter-options">
                      {facetOptions.length > 0 ? (
                        facetOptions.slice(0, 10).map((option, index) => {
                          const uniqueId = `filter-${filterType}-${option.value}-${index}`;
                          const isChecked = selectedFilters[filterType]?.includes(option.value) || false;
                          
                          return (
                            <div key={uniqueId} className="filter-option">
                              <input
                                type="checkbox"
                                id={uniqueId}
                                checked={isChecked}
                                onChange={() => handleFilterChange(filterType, option.value)}
                              />
                              <label htmlFor={uniqueId}>
                                {option.label || option.value || 'Sin nombre'}
                              </label>
                              {option.count > 0 && (
                                <span className="filter-option-count">
                                  ({option.count})
                                </span>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <p className="filter-info">
                          Las opciones se cargarán al realizar una búsqueda
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="filter-dropdown-footer">
            <button 
              className="apply-filters-btn"
              onClick={applyFilters}
              type="button"
            >
              Listo
            </button>
            <p className="filter-help-text">
              Los filtros se aplicarán al hacer clic en "Buscar"
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

SearchFilterDropdown.propTypes = {
  defaultFacets: PropTypes.objectOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    field: PropTypes.string.isRequired,
    showAll: PropTypes.bool,
    limit: PropTypes.number,
  })).isRequired,
};

export default SearchFilterDropdown;
